import { ConfirmChangesDialog } from 'components/organisms/NavigationBlocker';
import { GeneralPopup, PageHeader, PageWrapper } from 'components/partials';
import { AutoComplete, Button, EmptyState, Icon, Table, Tabs, WarningMessage } from 'components/widgets';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import { IconSizes } from '../components/foundations/Icon';
import DownloadIcon from '../resources/icons/download.svg';
import { useAsync } from '../utils/customHooks';
import { convertToCsv, download } from '../utils/export';
import WizardWarningMessage, { MessageTypes } from './components/WizardWarningMessage';
import AgencyDialog from './containers/AgencyDialog';
import ConfigurationRequiredDialog from './containers/ConfigurationRequiredDialog';
import ManagePermissionsPage from './containers/ManagePermissionsPage';
import TeamManagementAPI from './service';
import css from './styles.css';
import { getTableColumns } from './tableUtils';
import { MembersByGroupSchema, MembersGroupsSchema } from './types';
import { getSortedMembers, MEMBER_TYPES, trackTeamManagementMixpanelEvent } from './utils';

const api = new TeamManagementAPI();

const getExportTeamFileName = () => `singular_team_${new Date().toISOString()}.csv`;

const DEFAULT_SORT_CONFIG = { name: 'name', direction: 'down' };

const Page = ({
    memberTypes = [],
    activeMemberType = MEMBER_TYPES.USER,
    onActiveMemberTypeChange = () => {},
    membersByGroup = {},
    membersGroups = [],
    pageLoaded = false,
    page = { show: false },
    onDeleteMember = () => {},
    onDeleteBtnClicked = () => {},
    onCreateNewMember = () => {},
    onManagePermissions = () => {},
    onResendInvitation = () => {},
    translate = () => {},
    confirmChangesDialogOpen = false,
    onConfirmChangesAccepted = () => {},
    onConfirmChangesRejected = () => {},
    savedMemberName = '',
}) => {
    const [exportTeamMembersResponse, runExportTeamMembers] = useAsync();
    const [memberToDelete, setMemberToDelete] = useState(null);
    const [searchTextByType, setSearchTextByType] = useState({});
    const [sortConfigByType, setSortConfigByType] = useState(() =>
        Object.fromEntries(memberTypes.map(({ type }) => [type, DEFAULT_SORT_CONFIG]))
    );
    const [collapsedGroupsByType, setCollapsedGroupsByType] = useState({});

    const exportStartTime = useRef(null);
    const searchText = searchTextByType[activeMemberType] || '';
    const sortConfig = sortConfigByType[activeMemberType] || DEFAULT_SORT_CONFIG;
    const collapsedGroups = collapsedGroupsByType[activeMemberType] || new Set();

    const {
        data: exportTeamMembers,
        isLoading: isLoadingExportAll,
        error: exportTeamMembersError,
    } = exportTeamMembersResponse;

    const setSearchText = value => setSearchTextByType(prev => ({ ...prev, [activeMemberType]: value }));

    useEffect(() => {
        if (savedMemberName) {
            setSearchText(savedMemberName.toLowerCase());
        }
    }, [savedMemberName]);

    const handleDeleteClicked = member => {
        onDeleteBtnClicked();
        setMemberToDelete(member);
    };

    const onDeleteApproved = () => {
        onDeleteMember(memberToDelete);
        setMemberToDelete(null);
    };

    const onDeleteRejected = () => setMemberToDelete(null);

    const toggleGroup = groupName => {
        setCollapsedGroupsByType(prev => {
            const currentSet = prev[activeMemberType] || new Set();
            const next = new Set(currentSet);
            if (next.has(groupName)) {
                next.delete(groupName);
            } else {
                next.add(groupName);
            }
            return { ...prev, [activeMemberType]: next };
        });
    };

    const onExportClick = () => runExportTeamMembers(api.exportTeamMembers());

    const onChangeSort = name => {
        setSortConfigByType(prev => {
            const current = prev[activeMemberType] || {};
            let direction = 'down';
            if (current.name === name) {
                if (current.direction === null) direction = 'down';
                else if (current.direction === 'down') direction = 'up';
                else direction = null;
            }
            return { ...prev, [activeMemberType]: { name, direction } };
        });
    };

    const tableColumns = getTableColumns({
        memberType: activeMemberType,
        sortConfig,
        onChangeSort,
        onManagePermissions,
        onDelete: handleDeleteClicked,
        onResendInvitation,
        pageLoaded,
        translate,
        searchText,
    });

    useEffect(() => {
        if (isLoadingExportAll) {
            exportStartTime.current = performance.now();
        }

        if (exportTeamMembers) {
            const fileName = getExportTeamFileName();
            const csvData = convertToCsv(exportTeamMembers.fields, exportTeamMembers.data);
            download(fileName, csvData);

            trackTeamManagementMixpanelEvent('Export Team Button Clicked', {
                durationInMilliseconds: performance.now() - exportStartTime.current,
            });
        }
    }, [exportTeamMembersResponse]);

    const addMemberButton = (
        <Button
            type="primary"
            level="level1"
            className={css.newMemberButton}
            textClassName={css.newMemberButtonText}
            onClick={onCreateNewMember}
            disabled={!pageLoaded}
        >
            <Icon name="add-fill" variant={pageLoaded ? 'white' : 'light'} size={IconSizes.SMALL} />
            <Translate id={`STATIC.BUTTONS.${activeMemberType}_CREATE_NEW_MEMBER`} />
        </Button>
    );

    const renderInfoMessages = infoMessages =>
        infoMessages.map(infoMessage => (
            <WarningMessage
                key={infoMessage.message}
                type="success"
                className={css.infoMessage}
                showIcon
                showTypeIcon
                {...infoMessage}
            />
        ));

    const buildAgencyEmptyState = () => ({
        key: 'empty_agencies',
        subHeaderText: true,
        isEmptyState: true,
        emptyStateComponent: (
            <EmptyState
                customIcon={<Icon name="briefcase" variant="light" interactive={false} />}
                containerClass={css.emptyStateContainer}
                textContainerClass={css.emptyStateContainer}
                headerClassName={css.emptyStateText}
                subHeaderClassName={css.emptyStateText}
                header="STATIC.PAGES.TEAM_MEMBERS.EMPTY_STATE.HEADER_AGENCY"
                headerStyle={{ fontSize: 20, fontWeight: 600 }}
                subHeader="STATIC.PAGES.TEAM_MEMBERS.EMPTY_STATE.SUBHEADER_AGENCY"
            >
                {addMemberButton}
            </EmptyState>
        ),
    });

    const buildMemberRow = (member, groupName) => {
        if (activeMemberType === MEMBER_TYPES.USER) {
            const { disabled, ...memberData } = member;
            return {
                ...memberData,
                name: member.isMe ? `${member.name} (Me)` : member.name,
                key: `member_${member.id}`,
                groupName,
                resendDisabled: disabled,
            };
        }
        return { ...member, key: `member_${member.id}` };
    };

    const buildTableData = () => {
        const hasNoAgencies =
            activeMemberType === MEMBER_TYPES.AGENCY &&
            Object.values(membersByGroup).every(members => members.length === 0);

        if (hasNoAgencies) {
            return [buildAgencyEmptyState()];
        }

        const tableData = [];
        membersGroups.forEach(({ name: groupName, displayName: groupDisplayName }) => {
            const members = membersByGroup[groupName];
            const filteredMembers = getSortedMembers(
                members.filter(member => member.searchText.includes(searchText)),
                sortConfig
            );

            tableData.push({
                key: `group_${groupName}`,
                subHeaderText: true,
                isGroupHeader: true,
                groupName,
                groupTitle: `${translate(groupDisplayName)} (${filteredMembers.length})`,
                isCollapsed: collapsedGroups.has(groupName),
                onToggle: () => toggleGroup(groupName),
            });

            if (collapsedGroups.has(groupName)) return;

            if (filteredMembers.length === 0 && searchText) {
                tableData.push({
                    key: `empty_${groupName}`,
                    subHeaderText: true,
                    isEmptyState: true,
                    emptyStateText: translate('STATIC.PAGES.TEAM_MEMBERS.EMPTY_STATE.NO_SEARCH_RESULTS'),
                });
                return;
            }

            filteredMembers.forEach(member => {
                tableData.push(buildMemberRow(member, groupName));
            });
        });

        return tableData;
    };

    return (
        <PageWrapper className={css.pageWrapper} containerWrapperClass={css.containerWrapper} disablePadding>
            {page.show ? (
                <ManagePermissionsPage />
            ) : (
                <div className={css.mainContainer}>
                    <div className={css.headerContainer}>
                        <PageHeader
                            text="STATIC.MANAGE_TEAM"
                            leftComponents={[]}
                            rightComponent={[]}
                            subText={{
                                faqLink:
                                    'https://support.singular.net/hc/en-us/articles/360020835952-Team-Management-FAQ',
                            }}
                        />
                        {memberTypes?.length > 1 && (
                            <Tabs
                                containerStyle={{ marginTop: 10 }}
                                selected={activeMemberType}
                                onClick={onActiveMemberTypeChange}
                                tabs={memberTypes.map(({ type, count }) => ({
                                    id: type,
                                    label: `STATIC.PAGES.TEAM_MEMBERS.TAB_LABEL_${type.toUpperCase()}`,
                                    labelData: {
                                        count: pageLoaded ? `(${count})` : '',
                                    },
                                }))}
                            />
                        )}
                        <div className={css.filterRow}>
                            <AutoComplete
                                placeholder={translate(
                                    `STATIC.PAGES.TEAM_MEMBERS.PLACEHOLDER.${activeMemberType.toUpperCase()}_SEARCH`
                                )}
                                containerStyle={{ flexBasis: '500px', marginRight: '20px' }}
                                selectOptions={{
                                    options: [],
                                    isSearchable: true,
                                    inputValue: searchText,
                                    noOptionsMessage: () => null,
                                }}
                                onInputChange={value => setSearchText(value.toLowerCase())}
                            />
                            <div className={css.buttonsContainer}>
                                {activeMemberType === MEMBER_TYPES.USER && (
                                    <Button
                                        type="secondary"
                                        className={css.exportTeamMembersButton}
                                        showSpinner={!!isLoadingExportAll}
                                        Icon={DownloadIcon}
                                        onClick={onExportClick}
                                        iconClass={css.downloadIcon}
                                        disabled={!pageLoaded}
                                    >
                                        <Translate id="STATIC.BUTTONS.EXPORT" />
                                    </Button>
                                )}
                                {addMemberButton}
                            </div>
                        </div>
                        {exportTeamMembersError && (
                            <WizardWarningMessage
                                type={MessageTypes.ERROR}
                                show
                                duration={1000}
                                message="STATIC.PAGES.TEAM_MEMBERS.EXPORT_ERROR"
                                className={css.exportTeamMembersError}
                            />
                        )}
                    </div>
                    <div className={css.membersContainer}>
                        {renderInfoMessages(membersGroups.flatMap(g => g.infoMessages))}
                        <Table
                            key={activeMemberType}
                            data={pageLoaded ? buildTableData() : Array(4).fill({})}
                            metadata={tableColumns}
                            virtualized
                        />
                    </div>
                </div>
            )}
            <GeneralPopup
                open={!!memberToDelete}
                type="delete"
                title="STATIC.PAGES.TEAM_MEMBERS.DELETE_MODAL.TITLE"
                text="STATIC.PAGES.TEAM_MEMBERS.DELETE_MODAL.MESSAGE"
                acceptText="STATIC.PAGES.TEAM_MEMBERS.DELETE_MODAL.ACCEPT_BUTTON_TEXT"
                rejectText="STATIC.PAGES.TEAM_MEMBERS.DELETE_MODAL.REJECT_BUTTON_TEXT"
                onAccept={onDeleteApproved}
                onReject={onDeleteRejected}
            />
            <AgencyDialog />
            <ConfigurationRequiredDialog />
            <ConfirmChangesDialog
                open={confirmChangesDialogOpen}
                onAccept={onConfirmChangesAccepted}
                onReject={onConfirmChangesRejected}
            />
        </PageWrapper>
    );
};

Page.propTypes = {
    pageLoaded: PropTypes.bool,
    memberTypes: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.string,
            count: PropTypes.number,
        })
    ),
    membersByGroup: MembersByGroupSchema.isRequired,
    membersGroups: MembersGroupsSchema.isRequired,
    activeMemberType: PropTypes.string,
    onActiveMemberTypeChange: PropTypes.func,
    onDeleteMember: PropTypes.func,
    onDeleteBtnClicked: PropTypes.func,
    onManagePermissions: PropTypes.func,
    onResendInvitation: PropTypes.func,
    onCreateNewMember: PropTypes.func,
    translate: PropTypes.func,
    confirmChangesDialogOpen: PropTypes.bool,
    onConfirmChangesAccepted: PropTypes.func,
    onConfirmChangesRejected: PropTypes.func,
    savedMemberName: PropTypes.string,
    page: PropTypes.shape({
        show: PropTypes.bool,
        loading: PropTypes.bool,
        selectedTabIndex: PropTypes.number,
        stepValidationStatus: PropTypes.shape({
            isValid: PropTypes.bool,
            title: PropTypes.string,
        }),
    }),
};

export default withLocalize(Page);
